import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DashboardLayout from "@/components/DashboardLayout";
import { trpc } from "@/lib/trpc";
import { useMemo, useRef, useState } from "react";
import { MessageSquare, ArrowDownLeft, ArrowUpRight, Search, RefreshCw, Phone } from "lucide-react";
import { useLocation } from "wouter";
import { useVirtualizer } from "@tanstack/react-virtual";

export default function Messages() {
  const [direction, setDirection] = useState<"all" | "inbound" | "outbound">("all");
  const [search, setSearch] = useState("");
  const [, navigate] = useLocation();

  const { data: messages, isLoading, refetch, isFetching } = trpc.messages.list.useQuery(
    direction === "all" ? {} : { direction },
    { refetchInterval: 30000 }
  );

  const filtered = useMemo(() => {
    if (!messages) return [];
    if (!search) return messages;
    const q = search.toLowerCase();
    return messages.filter((m) =>
      (m.contactName ?? "").toLowerCase().includes(q) ||
      (m.contactPhone ?? "").toLowerCase().includes(q) ||
      (m.content ?? "").toLowerCase().includes(q),
    );
  }, [messages, search]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 124,
    overscan: 6,
  });

  const inboundCount = messages?.filter((m) => m.direction === "inbound").length ?? 0;
  const outboundCount = messages?.filter((m) => m.direction === "outbound").length ?? 0;

  const statusColor: Record<string, string> = {
    received: "bg-blue-100 text-blue-700",
    sent: "bg-gray-100 text-gray-700",
    delivered: "bg-yellow-100 text-yellow-700",
    read: "bg-green-100 text-green-700",
    failed: "bg-red-100 text-red-700",
  };

  const statusLabel: Record<string, string> = {
    received: "Recebida",
    sent: "Enviada",
    delivered: "Entregue",
    read: "Lida",
    failed: "Falhou",
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-5 h-full min-h-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">Mensagens</h1>
            <p className="text-muted-foreground text-sm mt-0.5">
              Caixa de entrada e saída do WhatsApp
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${isFetching ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{messages?.length ?? 0}</div>
              <div className="text-xs text-muted-foreground mt-0.5">Total</div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-600">{inboundCount}</div>
              <div className="text-xs text-muted-foreground mt-0.5">Recebidas</div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-600">{outboundCount}</div>
              <div className="text-xs text-muted-foreground mt-0.5">Enviadas</div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-500">
                {messages?.filter((m) => m.status === "failed").length ?? 0}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">Falhas</div>
            </CardContent>
          </Card>
        </div>

        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por contato ou conteúdo..."
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select value={direction} onValueChange={(v) => setDirection(v as any)}>
            <SelectTrigger className="w-full sm:w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as mensagens</SelectItem>
              <SelectItem value="inbound">Recebidas</SelectItem>
              <SelectItem value="outbound">Enviadas</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Lista de mensagens */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-20 bg-muted animate-pulse rounded-xl" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-30" />
            <p className="text-muted-foreground font-medium">Nenhuma mensagem encontrada</p>
            <p className="text-sm text-muted-foreground mt-1">
              As mensagens aparecerão aqui quando o webhook da Meta estiver configurado e ativo.
            </p>
          </div>
        ) : (
          <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
            <div style={{ height: virtualizer.getTotalSize(), position: "relative", width: "100%" }}>
              {virtualizer.getVirtualItems().map((vi) => {
                const msg = filtered[vi.index];
                return (
                  <div
                    key={msg.id}
                    ref={virtualizer.measureElement}
                    data-index={vi.index}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      transform: `translateY(${vi.start}px)`,
                      paddingBottom: 8,
                    }}
                  >
                    <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`mt-0.5 p-2 rounded-full flex-shrink-0 ${
                            msg.direction === "inbound"
                              ? "bg-blue-100 text-blue-600"
                              : "bg-green-100 text-green-600"
                          }`}>
                            {msg.direction === "inbound"
                              ? <ArrowDownLeft className="h-4 w-4" />
                              : <ArrowUpRight className="h-4 w-4" />
                            }
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div className="flex items-center gap-2">
                                <span
                                  className="font-medium text-sm cursor-pointer hover:underline"
                                  onClick={() => msg.contactId && navigate(`/contacts/${msg.contactId}`)}
                                >
                                  {msg.contactName ?? "Contato desconhecido"}
                                </span>
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {msg.contactPhone ?? "—"}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor[msg.status] ?? "bg-gray-100 text-gray-700"}`}>
                                  {statusLabel[msg.status] ?? msg.status}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(msg.createdAt).toLocaleString("pt-BR")}
                                </span>
                              </div>
                            </div>
                            {msg.content && (
                              <p className="text-sm text-muted-foreground mt-1 truncate">{msg.content}</p>
                            )}
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <Badge variant="outline" className="text-xs py-0">
                                {msg.direction === "inbound" ? "Recebida" : "Enviada"}
                              </Badge>
                              {msg.messageType && (
                                <Badge variant="outline" className="text-xs py-0 capitalize">
                                  {msg.messageType}
                                </Badge>
                              )}
                              {msg.metaMessageId && (
                                <span className="text-xs text-muted-foreground font-mono">
                                  ID: {msg.metaMessageId.slice(0, 16)}...
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Info sobre webhook */}
        {!isLoading && (messages?.length ?? 0) === 0 && (
          <Card className="border border-dashed border-muted-foreground/30 bg-muted/20">
            <CardContent className="p-5">
              <h3 className="font-semibold text-sm mb-2">Como configurar o Webhook</h3>
              <p className="text-sm text-muted-foreground">
                Para receber mensagens aqui, configure o webhook na Meta Business Platform com a URL:
              </p>
              <code className="block mt-2 p-2 bg-background rounded text-xs font-mono border">
                {window.location.origin}/api/webhooks/whatsapp
              </code>
              <p className="text-sm text-muted-foreground mt-2">
                O Verify Token deve ser configurado em <strong>Configurações → API Meta → Verify Token</strong>.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
